import { useContext, useState } from 'react';
import { useParams } from 'react-router';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addDays, addMonths } from 'date-fns';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { useRole } from '@/lib/stores/useRole';

import {
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiStartChangeExpirationDateMidtermMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import type { Policy } from '@/lib/heyapi/types.gen';
import { UserRoleType } from '@/lib/heyapi/types.gen';

import { formatDate, toDateString } from '../../../utils/dates';
import { AgentAuthorityContext } from '../../AgentAuthorityContext';
import { useGlobalLoading } from '../../GlobalLoadingContext';
import { formatErrorMessage } from '../../Header/utils';
import { EffectiveDateInput } from '../components/effectiveDateInput';

// The API returns date-only fields (policyEffectiveDate,
// policyExpirationDate) as ISO date strings like "2025-01-15" — the heyapi
// response transformer only converts datetime fields (createdAt etc.).
// Parse as a local-time Date so downstream date-fns math and toDateString
// don't drift a day in western timezones.
function parseIsoDateAsLocal(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function ChangeExpirationDateForm({
  policy,
}: {
  policy: Policy | undefined;
}) {
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const { setGlobalLoading } = useGlobalLoading();
  const { hasExactRole } = useRole();
  const { refetchAgentAuthority } = useContext(AgentAuthorityContext);
  const { id } = useParams();
  const queryClient = useQueryClient();

  const qk = glacierPolicyApiGetPolicyQueryKey({
    path: { account_id: id! },
  });

  // Runtime values here are strings (see comment on parseIsoDateAsLocal);
  // the generated `Date` type is a lie for date-only fields.
  const policyEffectiveDate = policy?.details?.policyEffectiveDate as
    | string
    | undefined;
  const currentExpirationDate = policy?.details?.policyExpirationDate as
    | string
    | undefined;

  const min = policyEffectiveDate
    ? toDateString(addDays(parseIsoDateAsLocal(policyEffectiveDate), 30))
    : undefined;
  const max = policyEffectiveDate
    ? toDateString(addMonths(parseIsoDateAsLocal(policyEffectiveDate), 18))
    : undefined;

  const { mutate: startChangeExpirationDate, isPending } = useMutation({
    ...glacierPolicyApiStartChangeExpirationDateMidtermMutation(),
    onError: (error) => {
      toast({
        title: 'Error',
        description: formatErrorMessage(
          error,
          'An error occurred while starting the Change Expiration Date transaction'
        ),
      });
    },
    onSuccess() {
      if (hasExactRole(UserRoleType.AGENT)) refetchAgentAuthority?.();
      queryClient.refetchQueries({ queryKey: qk });
      toast({
        title: 'Success',
        description: (
          <div className="mt-2 max-w-[340px] rounded-md bg-slate-950 p-4 text-sm text-white">
            Loading Change Expiration Date transaction
          </div>
        ),
      });
      // Radix Popover (modal) holds focus/scroll locks that can swallow the
      // React Router navigate; fall back to a hard redirect like
      // EndorsementForm does so the new midterm page actually loads.
      setTimeout(() => {
        window.location.href = `/policy/${id}/pricing-preview`;
      }, 300);
    },
    onSettled() {
      // Always clear the global loading overlay — if we forget this on success,
      // the page appears stuck because the spinner never hides.
      setGlobalLoading(false);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationError(null);

    if (!newExpirationDate || !policyEffectiveDate || !currentExpirationDate) {
      setValidationError('A new expiration date is required');
      return;
    }
    if (newExpirationDate === currentExpirationDate) {
      setValidationError(
        'New expiration date must differ from the current expiration date'
      );
      return;
    }
    if (min && newExpirationDate < min) {
      setValidationError(
        'New expiration date must be at least 30 days after the policy effective date'
      );
      return;
    }
    if (max && newExpirationDate > max) {
      setValidationError(
        'New expiration date cannot be more than 18 months after the policy effective date'
      );
      return;
    }

    setGlobalLoading(true);
    startChangeExpirationDate({
      body: {
        new_expiration_date: newExpirationDate,
      },
      path: { account_id: id! },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="text-sm text-muted-foreground">
          Current expiration:{' '}
          <span className="font-semibold text-foreground">
            {currentExpirationDate ? formatDate(currentExpirationDate) : '—'}
          </span>
        </div>
        <EffectiveDateInput
          value={newExpirationDate}
          onChange={(e) => setNewExpirationDate(e.target.value)}
          orientation="horizontal"
          label="New expiration date"
          min={min}
          max={max}
          required
          stretch
        />
        {validationError ? (
          <p className="text-sm text-red-500" role="alert">
            {validationError}
          </p>
        ) : null}
      </div>
      <Button type="submit" disabled={isPending}>
        {isPending ? 'Loading...' : 'Confirm'}
      </Button>
    </form>
  );
}
